import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Vote, Plus, ArrowLeft, BarChart3, PieChart, Calendar, 
  CheckCircle, Radio, Square, Type, Image as ImageIcon, Users
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { votingAPI } from '../services/api';

type QuestionType = 'single' | 'multiple' | 'text';

interface Voting {
  id: string;
  title: string;
  description: string;
  status: 'active' | 'closed' | 'draft';
  startDate: string;
  endDate: string;
  totalVotes: number;
  totalResidents: number;
  questions: Question[];
  hasVoted: boolean;
}

interface Question {
  id: string;
  text: string;
  type: QuestionType;
  options: string[];
  allowImages: boolean;
  required: boolean;
}

// Mock data removed — page fetches real data from votingAPI

export default function VotacionesAvanzadas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [votings, setVotings] = useState<Voting[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVoting, setActiveVoting] = useState<Voting | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [answers, setAnswers] = useState<Record<string, any>>({});

  const { isAdmin } = useAuth();

  useEffect(() => {
    loadVotings();
  }, []);

  const loadVotings = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const response = await votingAPI.getAll(condoId);
      const payload = response?.data?.data ?? response?.data ?? response;
      const rows = Array.isArray(payload) ? payload : payload && typeof payload === 'object'
        ? (Array.isArray(payload.items) ? payload.items : payload.votings) : [];
      setVotings(Array.isArray(rows) ? rows.map((v: any) => ({ ...v, questions: Array.isArray(v?.questions) ? v.questions : [] })) : []);
    } catch (err) {
      console.error('Error loading votings:', err);
      setVotings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async () => {
    try {
      if (!activeVoting) return;
      // Submit votes for each question
      for (const question of activeVoting.questions) {
        const answer = answers[question.id];
        if (answer) {
          await votingAPI.vote(activeVoting.id, answer);
        }
      }
      alert('✅ Voto registrado correctamente');
      setActiveVoting(null);
      loadVotings();
    } catch (err) {
      console.error('Error submitting vote:', err);
      alert('Error al registrar el voto');
    }
  };

  const getTypeIcon = (type: QuestionType) => {
    switch (type) {
      case 'single': return <Radio className="w-4 h-4" />;
      case 'multiple': return <CheckCircle className="w-4 h-4" />;
      default: return <Type className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: QuestionType) => {
    switch (type) {
      case 'single': return 'Selección simple';
      case 'multiple': return 'Selección múltiple';
      default: return 'Texto libre';
    }
  };

  if (activeVoting) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
        <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setActiveVoting(null)} className="p-2 text-[#2D3748] hover:bg-[var(--v-surface-2)] rounded-xl">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-[#2D3748]">{activeVoting.title}</h1>
            </div>
          </div>
        </header>

        <main className="p-6 max-w-2xl mx-auto">
          <div className="bg-[#36CCD8]/10 border border-[#36CCD8]/30 rounded-2xl p-4 mb-6">
            <p className="text-sm text-[#2D3748]">{activeVoting.description}</p>
            <p className="text-xs text-gray-500 mt-2">Válida hasta: {new Date(activeVoting.endDate).toLocaleDateString('es-MX')}</p>
          </div>

          <div className="space-y-6">
            {activeVoting.questions.map((question, idx) => (
              <div key={question.id} className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 bg-[#36CCD8] text-white rounded-full flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">{getTypeIcon(question.type)} {getTypeLabel(question.type)}</span>
                  {question.required && <span className="text-xs text-red-500">*Obligatorio</span>}
                </div>
                
                <h3 className="font-bold text-[#2D3748] mb-4">{question.text}</h3>
                
                {question.type === 'text' ? (
                  <textarea className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl h-32 resize-none" placeholder="Escribe tu respuesta..." />
                ) : (
                  <div className="space-y-2">
                    {question.options.map((option, optIdx) => (
                      <label key={optIdx} className="flex items-center gap-3 p-3 bg-[var(--v-surface-2)] rounded-xl cursor-pointer hover:bg-[#36CCD8]/10 transition-colors">
                        <input 
                          type={question.type === 'single' ? 'radio' : 'checkbox'} 
                          name={question.id}
                          className="w-5 h-5 text-[#36CCD8]"
                        />
                        <span className="text-[#2D3748]">{option}</span>
                        {question.allowImages && (
                          <button className="ml-auto text-gray-400 hover:text-[#36CCD8]">
                            <ImageIcon className="w-5 h-5" />
                          </button>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button 
            onClick={handleVote}
            className="w-full mt-6 py-4 bg-[#36CCD8] text-white font-bold rounded-2xl hover:bg-[#2DB8C4]"
          >
            Enviar Voto
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-[#2D3748]">Votaciones</h1>
          </div>
          {isAdmin() && (
            <button className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl flex items-center gap-2">
              <Plus className="w-4 h-4" /> Nueva
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        <div className="space-y-4">
          {votings.map(voting => (
            <div key={voting.id} className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-[#2D3748]">{voting.title}</h3>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                      voting.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-[var(--v-surface-2)] text-gray-600'
                    }`}>
                      {voting.status === 'active' ? 'Activa' : 'Cerrada'}
                    </span>
                    {voting.hasVoted && <span className="px-2 py-0.5 bg-[#36CCD8]/10 text-[#36CCD8] text-xs font-bold rounded-full">Ya votaste</span>}
                  </div>
                  <p className="text-sm text-gray-500">{voting.description}</p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setShowResults(true)}
                    className="p-2 text-gray-400 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl"
                  >
                    <BarChart3 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {voting.startDate} - {voting.endDate}</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {voting.totalVotes} de {voting.totalResidents} votaron</span>
              </div>

              <div className="w-full bg-[var(--v-surface-2)] rounded-full h-2 mb-4">
                <div 
                  className="bg-[#36CCD8] h-2 rounded-full transition-all"
                  style={{ width: `${(voting.totalVotes / voting.totalResidents) * 100}%` }}
                />
              </div>

              {!voting.hasVoted && voting.status === 'active' && (
                <button 
                  onClick={() => setActiveVoting(voting)}
                  className="w-full py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4]"
                >
                  Votar Ahora
                </button>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
